import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userApi } from '../utils/api';
import { handleApiError } from '../utils/helpers';
import { requestNotificationPermission } from '../utils/notifications';
import InstallInstructions from '../components/InstallInstructions';
import { isMobileDevice, isRunningAsInstalledPwa } from '../utils/pwa';

const UserSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    notificationPreferences: {
      email: true,
      push: true,
      feedingRequests: true,
      catUpdates: true
    },
    displayPreferences: {
      darkMode: false,
      showCatPhotos: true,
      largeFonts: false
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [formChanged, setFormChanged] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  
  // Check if running as installed PWA
  useEffect(() => {
    setIsInstalled(isRunningAsInstalledPwa());
  }, []);
  
  // Fetch user settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // In a real app, you would fetch user settings from the API
        // For now, we'll just simulate it with a timeout
        const profileData = await userApi.getUserProfile();
        
        // Extract notification preferences from the profile or use defaults
        setSettings({
          notificationPreferences: {
            email: profileData.notificationPreferences?.email ?? true,
            push: profileData.notificationPreferences?.push ?? true,
            feedingRequests: true,
            catUpdates: true
          },
          displayPreferences: {
            darkMode: false,
            showCatPhotos: true,
            largeFonts: false
          }
        });
      } catch (error) {
        console.error('Error fetching settings:', error);
        setError(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);
  
  // Handle notification preference changes
  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      notificationPreferences: {
        ...prev.notificationPreferences,
        [name]: checked
      }
    }));
    setFormChanged(true);
    
    // If enabling push notifications, request permission
    if (name === 'push' && checked) {
      requestPushPermission();
    }
  };
  
  // Handle display preference changes
  const handleDisplayChange = (e) => {
    const { name, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      displayPreferences: {
        ...prev.displayPreferences,
        [name]: checked
      }
    }));
    setFormChanged(true);
  };
  
  // Request push notification permission
  const requestPushPermission = async () => {
    try {
      const permission = await requestNotificationPermission();
      if (permission !== 'granted') {
        setSettings(prev => ({
          ...prev,
          notificationPreferences: {
            ...prev.notificationPreferences,
            push: false
          }
        }));
        setError('Push notification permission was denied. You won\'t receive notifications.');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setSettings(prev => ({
        ...prev,
        notificationPreferences: {
          ...prev.notificationPreferences,
          push: false
        }
      }));
      setError('Could not request notification permission. Push notifications are disabled.');
    }
  };
  
  // Save settings
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formChanged) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage('');
      
      // In a real app, you would save the settings to the API
      // For now, we'll just simulate it with a timeout
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSuccessMessage('Settings saved successfully!');
      setFormChanged(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setError(handleApiError(error));
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="settings-page">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-description">
          Manage your notification and display preferences
        </p>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && !loading && (
        <div className="error-message">
          <p>{error}</p>
          <button 
            className="button"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Success message */}
      {successMessage && (
        <div className="success-message">
          <p>{successMessage}</p>
        </div>
      )}
      
      {/* Settings form */}
      {!loading && (
        <form onSubmit={handleSubmit} className="settings-form">
          {/* App Installation Section */}
          {isMobileDevice() && (
            <div className="settings-section">
              <h2>App Installation</h2>
              <div className="settings-group">
                <div className="settings-install-status">
                  <p>
                    <strong>Status:</strong> {isInstalled ? 'Installed as app' : 'Not installed as app'}
                  </p>
                  {!isInstalled && (
                    <div className="settings-install-instructions">
                      <p>Install the app to your home screen for the best experience:</p>
                      <InstallInstructions />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="settings-section">
            <h2>Notification Preferences</h2>
            <div className="settings-group">
              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  name="email"
                  checked={settings.notificationPreferences.email}
                  onChange={handleNotificationChange}
                />
                <span>Email notifications</span>
              </label>
              <p className="settings-description">
                Receive updates and alerts via email
              </p>
            </div>
            
            <div className="settings-group">
              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  name="push"
                  checked={settings.notificationPreferences.push}
                  onChange={handleNotificationChange}
                />
                <span>Push notifications</span>
              </label>
              <p className="settings-description">
                Receive real-time notifications on your device
              </p>
            </div>
            
            <div className="settings-group">
              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  name="feedingRequests"
                  checked={settings.notificationPreferences.feedingRequests}
                  onChange={handleNotificationChange}
                />
                <span>Feeding request updates</span>
              </label>
              <p className="settings-description">
                Get notified when your feeding request is processed
              </p>
            </div>
            
            <div className="settings-group">
              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  name="catUpdates"
                  checked={settings.notificationPreferences.catUpdates}
                  onChange={handleNotificationChange}
                />
                <span>Cat updates</span>
              </label>
              <p className="settings-description">
                Be informed when new cats are added to the system
              </p>
            </div>
          </div>
          
          <div className="settings-section">
            <h2>Display Preferences</h2>
            <div className="settings-group">
              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  name="darkMode"
                  checked={settings.displayPreferences.darkMode}
                  onChange={handleDisplayChange}
                />
                <span>Dark mode</span>
              </label>
              <p className="settings-description">
                Use a darker color scheme (coming soon)
              </p>
            </div>
            
            <div className="settings-group">
              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  name="showCatPhotos"
                  checked={settings.displayPreferences.showCatPhotos}
                  onChange={handleDisplayChange}
                />
                <span>Always show cat photos</span>
              </label>
              <p className="settings-description">
                Display cat photos in the feeds and lists
              </p>
            </div>
            
            <div className="settings-group">
              <label className="settings-checkbox">
                <input
                  type="checkbox"
                  name="largeFonts"
                  checked={settings.displayPreferences.largeFonts}
                  onChange={handleDisplayChange}
                />
                <span>Large fonts</span>
              </label>
              <p className="settings-description">
                Use larger text for better readability (coming soon)
              </p>
            </div>
          </div>
          
          <div className="form-actions">
            <button
              type="submit"
              className="button primary"
              disabled={saving || !formChanged}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default UserSettings; 